import { useEffect, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { usePolling } from './usePolling';
import { apiFetch } from '../utils/api';

type SocialSSEMessage =
  | { type: 'connected'; timestamp?: number }
  | { type: 'social_snapshot'; pending_count?: number; total?: number; timestamp?: number }
  | { type: 'social_update'; conversation_id?: string; timestamp?: number }
  | { type: 'error'; message?: string };

export function useSocialInboxSSE(enabled: boolean) {
  const [pendingCount, setPendingCount] = useState(0);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const pendingRequestRef = useRef<AbortController | null>(null);

  const pollSnapshot = async () => {
    if (!enabled) return;
    const controller = new AbortController();
    pendingRequestRef.current = controller;
    try {
      const res = await apiFetch(API_ENDPOINTS.SOCIAL.INBOX + '?limit=1', { signal: controller.signal });
      if (!res.ok) return;
      const data = await res.json() as { summary?: { pending_count?: number } };
      setPendingCount(Number(data.summary?.pending_count ?? 0));
      setLastEvent(new Date().toISOString());
    } catch {
      // ignore fallback failures
    } finally {
      if (pendingRequestRef.current === controller) pendingRequestRef.current = null;
    }
  };

  usePolling(() => pollSnapshot(), 5 * 60_000, [enabled], {
    enabled: Boolean(enabled) && connectionState === 'disconnected',
    immediate: true,
    pauseWhenHidden: true,
  });

  useEffect(() => {
    if (!enabled) return;

    const eventSource = new EventSource(API_ENDPOINTS.SSE.SOCIAL);

    const apply = (data: string) => {
      setLastEvent(new Date().toISOString());
      try {
        const parsed = JSON.parse(data) as SocialSSEMessage;
        if (parsed.type === 'social_snapshot') {
          setPendingCount(Number(parsed.pending_count ?? 0));
        } else if (parsed.type === 'social_update') {
          void pollSnapshot();
        }
      } catch {
        // ignore malformed frames
      }
    };

    eventSource.onopen = () => setConnectionState('connected');
    eventSource.addEventListener('connected', (event) => apply((event as MessageEvent).data));
    eventSource.addEventListener('social_snapshot', (event) => apply((event as MessageEvent).data));
    eventSource.addEventListener('social_update', (event) => apply((event as MessageEvent).data));
    eventSource.onerror = () => setConnectionState('disconnected');

    return () => {
      eventSource.close();
      setConnectionState('disconnected');
      pendingRequestRef.current?.abort();
      pendingRequestRef.current = null;
    };
  }, [enabled]);

  return {
    pendingCount,
    lastEvent,
    connectionState,
    isConnected: connectionState === 'connected',
    refresh: pollSnapshot,
  };
}
